'use client';

import React, { useCallback, useMemo } from 'react';
import { useGetSystemSitesQuery } from 'src/api/redux';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useSite } from 'src/context/providers/SiteProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useResolveSystemId } from 'src/components/Profiles/shared/widgets/SystemVolume/useResolveSystemId';
import { buildVolumeConfig, sumRowVolume } from './volumeConfigUtils';

const PREVIEW_MAX_ITEMS = 3;

const CompactSystemSitesPreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const { openDrawer } = useDrawer();
  const { dispatch } = useSite();
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;

  const systemId = useResolveSystemId(
    entityDetails,
    widget?.network_rollup === true
  );
  const widgetFilterSetId = widgetConfig?.filterSetId;

  const volumeType = widget?.volume_type ?? 'claims';

  const volumeConfig = useMemo(
    () => buildVolumeConfig(widget, widgetConfig?.minYear, widgetConfig?.maxYear),
    [widget, widgetConfig?.minYear, widgetConfig?.maxYear]
  );

  const hasVolume = Object.keys(volumeConfig).length > 0;

  const handleSiteClick = useCallback(
    (row: Record<string, any>) => {
      dispatch({
        type: 'SET_CURRENT_PROVIDER',
        payload: { id: row.provider_id, type: row.type || 'clinic' },
      });
      openDrawer('profile');
    },
    [dispatch, openDrawer]
  );

  const { data, isLoading } = useGetSystemSitesQuery(
    {
      systemId,
      page: 1,
      pageSize: PREVIEW_MAX_ITEMS,
      sort: hasVolume ? 'volume' : 'name',
      order: hasVolume ? 'desc' : 'asc',
      sort_field: hasVolume ? volumeType : undefined,
      q: '',
      filterSets: widgetFilterSetId ? [widgetFilterSetId] : undefined,
      data: hasVolume ? volumeConfig : undefined,
    },
    { skip: !systemId }
  );

  const rows = useMemo(() => data?.results || [], [data]);

  if (isLoading) {
    return <WidgetLoading data-testid="compact-system-sites-preview-loading" aria-label="Loading system sites" />;
  }

  if (rows.length === 0) {
    return <WidgetEmpty data-testid="compact-system-sites-preview-empty" />;
  }

  return (
    <div
      className="flex flex-col"
      role="list"
      aria-label="System Sites"
      data-testid="compact-system-sites-preview"
    >
      {rows.map((row: Record<string, any>) => {
        const total = sumRowVolume(row, volumeType);
        return (
          <div
            key={row.provider_id || row.id}
            className="flex items-center justify-between py-1.5 border-b border-neutral-100 last:border-b-0"
            role="listitem"
          >
            <button
              type="button"
              onClick={() => handleSiteClick(row)}
              className="text-sm font-bold text-link truncate hover:underline text-left"
            >
              {row.name || 'N/A'}
            </button>
            {total > 0 && (
              <span className="text-sm font-semibold text-teal-900 tabular-nums flex-shrink-0 ml-4">
                {handleNumericValue(total)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CompactSystemSitesPreview;
